import React, { useState, useEffect } from 'react';
import { 
  ExternalLink, 
  RefreshCw, 
  Download, 
  Eye, 
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  FileText
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface GoogleDoc {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  webViewLink: string;
  exportLinks?: {
    [key: string]: string;
  };
  owners: Array<{
    displayName: string;
    emailAddress: string;
  }>;
  lastModifyingUser: {
    displayName: string;
    emailAddress: string;
  };
  size?: string;
  thumbnailLink?: string;
}

const GoogleDocsIntegration: React.FC = () => {
  const { colors } = useTheme();
  const [docs, setDocs] = useState<GoogleDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Mock Google Docs data
  const mockDocs: GoogleDoc[] = [
    {
      id: '1',
      name: 'React Study Notes',
      mimeType: 'application/vnd.google-apps.document',
      modifiedTime: '2025-01-20T10:30:00Z',
      webViewLink: 'https://docs.google.com/document/d/1/edit',
      exportLinks: {
        'application/pdf': 'https://docs.google.com/document/d/1/export?format=pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'https://docs.google.com/document/d/1/export?format=docx',
        'text/plain': 'https://docs.google.com/document/d/1/export?format=txt'
      },
      owners: [{ displayName: 'John Doe', emailAddress: 'john@example.com' }],
      lastModifyingUser: { displayName: 'John Doe', emailAddress: 'john@example.com' }
    },
    {
      id: '2',
      name: 'Project Planning Spreadsheet',
      mimeType: 'application/vnd.google-apps.spreadsheet',
      modifiedTime: '2025-01-19T15:45:00Z',
      webViewLink: 'https://docs.google.com/spreadsheets/d/2/edit',
      exportLinks: {
        'application/pdf': 'https://docs.google.com/spreadsheets/d/2/export?format=pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'https://docs.google.com/spreadsheets/d/2/export?format=xlsx'
      },
      owners: [{ displayName: 'Jane Smith', emailAddress: 'jane@example.com' }],
      lastModifyingUser: { displayName: 'John Doe', emailAddress: 'john@example.com' }
    },
    {
      id: '3',
      name: 'Presentation - ML Fundamentals',
      mimeType: 'application/vnd.google-apps.presentation',
      modifiedTime: '2025-01-18T09:20:00Z',
      webViewLink: 'https://docs.google.com/presentation/d/3/edit',
      exportLinks: {
        'application/pdf': 'https://docs.google.com/presentation/d/3/export?format=pdf',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'https://docs.google.com/presentation/d/3/export?format=pptx'
      },
      owners: [{ displayName: 'John Doe', emailAddress: 'john@example.com' }],
      lastModifyingUser: { displayName: 'John Doe', emailAddress: 'john@example.com' }
    },
    {
      id: '4',
      name: 'Research Paper Draft',
      mimeType: 'application/vnd.google-apps.document',
      modifiedTime: '2025-01-17T14:10:00Z',
      webViewLink: 'https://docs.google.com/document/d/4/edit',
      exportLinks: {
        'application/pdf': 'https://docs.google.com/document/d/4/export?format=pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'https://docs.google.com/document/d/4/export?format=docx'
      },
      owners: [{ displayName: 'John Doe', emailAddress: 'john@example.com' }],
      lastModifyingUser: { displayName: 'Jane Smith', emailAddress: 'jane@example.com' }
    }
  ];

  useEffect(() => {
    // Check if user is connected to Google Drive
    const connected = localStorage.getItem('google-drive-connected') === 'true';
    setIsConnected(connected);
    
    if (connected) {
      setDocs(mockDocs);
    }
  }, []);

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      // Simulate Google OAuth flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      localStorage.setItem('google-drive-connected', 'true');
      setIsConnected(true);
      setDocs(mockDocs);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('google-drive-connected');
    setIsConnected(false);
    setDocs([]);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      // Simulate API refresh
      await new Promise(resolve => setTimeout(resolve, 1000));
      setDocs(mockDocs);
    } finally {
      setIsLoading(false);
    }
  };

  const getDocIcon = (mimeType: string) => {
    if (mimeType.includes('document')) {
      return <FileText className="w-6 h-6" style={{ color: colors.blue }} />;
    } else if (mimeType.includes('spreadsheet')) {
      return <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white text-xs font-bold">S</div>;
    } else if (mimeType.includes('presentation')) {
      return <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white text-xs font-bold">P</div>;
    }
    return <FileText className="w-6 h-6" style={{ color: colors.subtext0 }} />;
  };

  const getDocTypeName = (mimeType: string) => {
    if (mimeType.includes('document')) return 'Document';
    if (mimeType.includes('spreadsheet')) return 'Spreadsheet';
    if (mimeType.includes('presentation')) return 'Presentation';
    return 'File';
  };

  const filteredDocs = docs.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.mimeType.includes(filterType);
    return matchesSearch && matchesType;
  });

  const handleImport = async (doc: GoogleDoc) => {
    try {
      // Simulate importing document to StudyBro
      console.log('Importing document:', doc.name);
      
      // In a real implementation, you would:
      // 1. Download the document content
      // 2. Convert it to markdown or store as-is
      // 3. Add it to the user's documents
      
      alert(`Document "${doc.name}" imported successfully!`);
    } catch (error) {
      console.error('Import failed:', error);
      alert('Failed to import document');
    }
  };

  if (!isConnected) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div 
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: colors.surface0 }}
          >
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="14,2 14,8 20,8" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="16" y1="13" x2="8" y2="13" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="16" y1="17" x2="8" y2="17" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="10,9 9,9 8,9" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Connect to Google Drive
          </h2>
          
          <p className="text-lg mb-8 max-w-md mx-auto" style={{ color: colors.subtext0 }}>
            Access your Google Docs, Sheets, and Slides directly from StudyBro. 
            Import documents and keep your study materials synchronized.
          </p>
          
          <button
            onClick={handleConnect}
            disabled={isLoading}
            className="px-8 py-4 rounded-lg font-medium text-white text-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-3 mx-auto"
            style={{ backgroundColor: colors.blue }}
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connect with Google
              </>
            )}
          </button>
          
          <div className="mt-8 text-sm" style={{ color: colors.subtext0 }}>
            <p>We'll only access documents you explicitly choose to import.</p>
            <p>Your privacy and data security are our top priorities.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            Google Drive Integration
          </h1>
          <p style={{ color: colors.subtext0 }}>
            Access and import your Google Docs, Sheets, and Slides
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={handleDisconnect}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.red
            }}
          >
            Disconnect
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: colors.subtext0 }}
            />
            <input
              type="text"
              placeholder="Search Google Drive documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
            style={{
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <option value="all">All Types</option>
            <option value="document">Documents</option>
            <option value="spreadsheet">Spreadsheets</option>
            <option value="presentation">Presentations</option>
          </select>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocs.map((doc) => (
          <div
            key={doc.id}
            className="p-6 rounded-xl border transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {getDocIcon(doc.mimeType)}
                <div>
                  <h3 className="font-semibold line-clamp-2" style={{ color: colors.text }}>
                    {doc.name}
                  </h3>
                  <span className="text-sm" style={{ color: colors.subtext0 }}>
                    {getDocTypeName(doc.mimeType)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-sm" style={{ color: colors.subtext0 }}>
                <User className="w-4 h-4" />
                <span>Owner: {doc.owners[0]?.displayName}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm" style={{ color: colors.subtext0 }}>
                <Calendar className="w-4 h-4" />
                <span>Modified: {new Date(doc.modifiedTime).toLocaleDateString()}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm" style={{ color: colors.subtext0 }}>
                <User className="w-4 h-4" />
                <span>Last edited by: {doc.lastModifyingUser.displayName}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.open(doc.webViewLink, '_blank')}
                className="flex-1 px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                <Eye className="w-4 h-4" />
                View
              </button>
              
              <button
                onClick={() => handleImport(doc)}
                className="flex-1 px-3 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ backgroundColor: colors.blue }}
              >
                <Plus className="w-4 h-4" />
                Import
              </button>
              
              {doc.exportLinks && (
                <div className="relative group">
                  <button
                    className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80"
                    style={{ 
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  
                  <div 
                    className="absolute right-0 top-full mt-2 w-48 rounded-lg border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10"
                    style={{ 
                      backgroundColor: colors.mantle,
                      borderColor: colors.surface0
                    }}
                  >
                    <div className="p-2">
                      {Object.entries(doc.exportLinks).map(([format, url]) => (
                        <a
                          key={format}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-3 py-2 rounded text-sm hover:opacity-80 transition-colors"
                          style={{ 
                            backgroundColor: colors.base,
                            color: colors.text
                          }}
                        >
                          {format.includes('pdf') ? 'PDF' :
                           format.includes('docx') ? 'Word' :
                           format.includes('xlsx') ? 'Excel' :
                           format.includes('pptx') ? 'PowerPoint' :
                           format.includes('txt') ? 'Text' : 'Download'}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredDocs.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: colors.surface0 }}
          >
            <Search className="w-8 h-8" style={{ color: colors.subtext0 }} />
          </div>
          <h3 className="text-lg font-medium mb-2" style={{ color: colors.text }}>
            No documents found
          </h3>
          <p style={{ color: colors.subtext0 }}>
            {searchTerm || filterType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'No Google Drive documents available'
            }
          </p>
        </div>
      )}

      {/* Connection Status */}
      <div 
        className="p-4 rounded-lg border"
        style={{ 
          backgroundColor: colors.surface0,
          borderColor: colors.surface1
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: colors.green }}
            />
            <span className="font-medium" style={{ color: colors.text }}>
              Connected to Google Drive
            </span>
          </div>
          
          <div className="text-sm" style={{ color: colors.subtext0 }}>
            {docs.length} documents available
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDocsIntegration;